import feature from 'topojson-client/src/feature';
import { trackEvent } from 'utils/analytics';

// Inital state
const getInitialState = () => {
	const state = {
		hasLoaded: false,
		hasLoadedEntities: false,
		hasLoadedSmall: false,
		hasFilledEntities: false,
		selected: null,
		hasSelected: false,
		highlighted: null,
		hasHighlighted: false,
		all: [],
		disputed: [],
		allRegions: [],
		allEntities: [],
		allOptions: {
			primaryFocus: [
				{
					value: 'Accelerating the clean energy transition',
					name: 'Clean energy transition'
				},
				{ value: 'Empowering women', name: 'Empowering women' },
				{
					value: 'Addressing energy poverty',
					name: 'Energy poverty'
				},
				{
					value: 'Moving Capital into Clean Energy/Climate Change',
					name: 'Moving capital into clean energy and climate change'
				},
				{
					value: 'Promoting gender equality and social inclusion',
					name: 'Gender equality and social inclusion'
				}
			],
			organizationType: [
				{ value: 'Development Institution', name: 'Development institution' },
				{ value: 'Foundation/Charity', name: 'Foundation/charity' },
				{ value: 'Grassroots organization', name: 'Grass root organization' },
				{ value: 'International NGO', name: 'International NGO' },
				{ value: 'Private Enterprise', name: 'Private enterprise' },
				{
					value: 'Research/Training Institution',
					name: 'Research/training institution'
				},
				{ value: 'Trade Association', name: 'Trade association' },
				{ value: '', name: '' }
			],
			annualBudget: [
				{ value: '>$5,000,000', name: '>$5,000,000' },
				{ value: '$1,000,000-$5,000,000', name: '$1,000,000 – $5,000,000' },
				{ value: '$500,000-$1,000,000', name: '$500,000 – $1,000,000' },
				{ value: '$100,000-$500,000', name: '$100,000 – $500,000' },
				{ value: '$10,000-$100,000', name: '$10,000 – $100,000' },
				{ value: '<$10,000', name: '<$10,000' },
				{ value: '', name: '' }
			],
			funderType: [
				{ value: 'Corporate', name: 'Corporate' },
				{ value: 'Development Institution', name: 'Development institution' },
				{ value: 'Foundation/Charity', name: 'Foundation/charity' },
				{ value: 'Individual', name: 'Individual' },
				{
					value: 'Non-Profit Program Organization',
					name: 'Non-profit organization'
				},
				{ value: 'Other', name: 'Other' },
				{
					value: 'Other Government Institutions',
					name: 'Government institution'
				},
				{ value: 'Special Purpose Vehicle', name: 'Special purpose vehicle' },
				{ value: '', name: '' }
			],
			implementerType: [
				{ value: 'Development institution', name: 'Development institution' },
				{ value: 'International NGO', name: 'International NGO' },
				{ value: 'Private sector', name: 'Private sector' },
				{ value: '', name: '' },
				{ value: 'Grassroots organization', name: 'Grass root organization' },
				{
					value: 'Training/Research institution',
					name: 'Training/research institution'
				},
				{ value: 'Trade Association', name: 'Trade association' },
				{ value: 'Government', name: 'Government' }
			],
			barriers: [
				{
					value: 'Access to multi-year funding/investment',
					name: 'Access to multi-year funding'
				},
				{
					value: 'High administrative costs',
					name: 'High administrative costs'
				},
				{ value: 'Lack of awareness', name: 'Lack of awareness' },
				{ value: 'Lack of growth capital', name: 'Lack of growth capital' },
				{
					value: 'Lack of high quality data',
					name: 'Lack of high quality data'
				},
				{ value: 'Limited capacity', name: 'Limited capacity' },
				{ value: 'Limited incentives', name: 'Limited incentives' },
				{ value: 'Other', name: 'Other' },
				{
					value: 'Political economy (cultural and social norms)',
					name: 'Social norms'
				},
				{ value: 'Unclear metrics', name: 'Unclear metrics' },
				{ value: '', name: '' }
			]
		},
		max: {
			region: 0,
			country: 0
		}
	};

	return state;
};

// Actions
const getCountry = (countries, cid) => countries.find(({ id }) => id === cid);
const select = (state, id) => {
	trackEvent('Countries', 'Select', id);
	return { ...state, selected: id, hasSelected: true };
};
const highlight = (state, id) => ({
	...state,
	highlighted: id,
	hasHighlighted: true
});
const clearSelected = state => ({ ...state, hasSelected: false });
const clearHighlighted = state => ({ ...state, hasHighlighted: false });
const clear = state => ({
	...state,
	highlighted: null,
	selected: null
});

const setAll = (state, countriesTopo) => {
	const next = {
		...state,
		hasLoaded: true,
		all: feature(countriesTopo, countriesTopo.objects.countries)
			.features.map(({ properties, ...rest }) => ({
				...rest,
				id: properties.country,
				...properties
			}))
			.sort((a, b) => a.country.localeCompare(b.country)),
		disputed: feature(countriesTopo, countriesTopo.objects.disputed).features,
		allRegions: feature(
			countriesTopo,
			countriesTopo.objects.regions
		).features.map(({ properties, ...rest }) => ({
			...rest,
			...properties
		}))
	};
	next.max = {
		region: next.allRegions.reduce((a, b) => Math.max(a, b.entities.length), 0),
		country: next.all.reduce((a, b) => Math.max(a, b.entities.length), 0)
	};

	if (state.allEntities.length > 0 && !next.hasFilledEntities) {
		next.all = next.all.map(feature => ({
			...feature,
			entities: feature.entities
				.map(eid => state.allEntities.find(entity => eid === +entity.id))
				.sort((a, b) => a.name.localeCompare(b.name))
		}));

		next.allRegions = next.allRegions.map(feature => ({
			...feature,
			entities: feature.entities
				.map(eid => state.allEntities.find(entity => eid === +entity.id))
				.sort((a, b) => a.name.localeCompare(b.name))
		}));
		next.hasFilledEntities = true;
	}

	return next;
};

const setEntities = (state, entities) => {
	const next = {
		...state,
		hasLoadedEntities: true,
		allEntities: entities
	};

	if (state.all.length > 0 && !next.hasFilledEntities) {
		next.all = next.all.map(feature => ({
			...feature,
			entities: feature.entities
				.map(eid => next.allEntities.find(entity => eid === +entity.id))
				.sort((a, b) => a.name.localeCompare(b.name))
		}));

		next.allRegions = next.allRegions.map(feature => ({
			...feature,
			entities: feature.entities
				.map(eid => next.allEntities.find(entity => eid === +entity.id))
				.sort((a, b) => a.name.localeCompare(b.name))
		}));
		next.hasFilledEntities = true;
	}

	return next;
};

const setSmall = (state, countriesTopo) => ({
	...state,
	hasLoadedSmall: true,
	smallTopo: {
		countries: feature(
			countriesTopo,
			countriesTopo.objects.countries
		).features.map(({ properties, ...rest }) => ({
			...rest,
			...properties
		})),
		regions: feature(countriesTopo, countriesTopo.objects.regions).features.map(
			({ properties, ...rest }) => ({
				...rest,
				...properties
			})
		)
	}
});

export const actions = {
	select,
	highlight,
	clearSelected,
	clearHighlighted,
	clear,
	setAll,
	setEntities,
	setSmall
};

// Selectors
const getHighlightedCountry = state =>
	getCountry(getFiltered(state), state.countries.highlighted);

const getSelectedCountry = state => {
	if (!state.countries.selected) {
		return null;
	}

	const country = getCountry(getFiltered(state), state.countries.selected);

	return {
		...country,
		totals: getTotals(state, country),
		empty: getEmpty(state, country)
	};
};

const getIsSelected = ({ countries }, id) => id === countries.selected;

const getFilteredEntries = ({ countries, filters }, entities) => {
	const { selected } = filters.primaryFocus;
	return entities.filter(
		entity => !selected || selected === entity.primaryFocus
	);
};

const getFiltered = state => {
	const { countries: { all, allRegions }, view } = state;
	const data = view === 'region' ? allRegions : all;

	return data.map(country => {
		const { id, entities } = country;
		return {
			...country,
			isVisible: true,
			isSelected: getIsSelected(state, id),
			filteredEntities: getFilteredEntries(state, entities)
		};
	});
};

const getTotals = (state, country) => {
	const { allOptions } = state.countries;
	let totals = Object.entries(allOptions).reduce((obj, [key, value]) => {
		obj[key] = value
			.map(option => {
				if (option.value === '') {
					return;
				}

				const count = country.filteredEntities.filter(entity => {
					if (typeof entity[key] !== 'undefined') {
						return entity[key].includes(option.value);
					}
					return entity[key] === option;
				}).length;

				return {
					name: option.name,
					value: option.value,
					count
				};
			})
			.filter(Boolean);
		return obj;
	}, {});

	return Object.entries(totals).reduce((obj, [key, value]) => {
		const total = value.reduce((a, b) => a + b.count, 0);

		if (total === 0) {
			obj[key] = [];
		}
		else {
			obj[key] = value.map(option => ({
				...option,
				percentage: Math.round(option.count / total * 100)
			}));
		}

		return obj;
	}, {});
};

const getEmpty = (state, country) => {
	const { allOptions } = state.countries;

	return Object.entries(allOptions).reduce(
		(obj, [key, value]) => ({
			...obj,
			[key]: country.filteredEntities.filter(
				entity =>
					entity[key] === '' ||
					entity[key].length === 0 ||
					(entity[key].length === 1 && entity[key].includes(''))
			).length
		}),
		{}
	);
};

const getMax = state => state.countries.max[state.view];

export const selectors = {
	getSelectedCountry,
	getHighlightedCountry,
	getFiltered,
	getMax
};

export default { getInitialState, actions, selectors };



// WEBPACK FOOTER //
// ./state/countries.js